import React from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, unwrap, errMsg } from "@/lib/api";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { CreditCard, Loader2, ShieldCheck } from "lucide-react";
import { guardianCopyFor } from "./guardianCopy";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const { t, language } = useI18n();
  const copy = guardianCopyFor(language);
  const navigate = useNavigate();
  const simulate = params.get("simulate") === "1";
  const paymentId = params.get("payment_id") || "";

  const submit = useMutation({
    mutationFn: async (status) =>
      unwrap(await api.post("/payments/simulate-callback", { payment_id: paymentId, status })),
    onSuccess: (d) => {
      toast.success(d.status === "paid" ? t("guardian.paymentSuccessful") : d.status === "cancelled" ? t("guardian.paymentCancelled") : t("guardian.paymentFailed"));
      navigate("/parent/fees", { replace: true });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (!simulate) {
    return (
      <div className="max-w-md mx-auto mt-16 px-4" data-testid="payment-result">
        <Card className="border-[var(--subtle)]">
          <CardContent className="p-6 text-center">
            <div className="text-sm text-[var(--text-secondary)]">{t("guardian.paymentNoPending")}</div>
            <Button className="mt-4" size="sm" onClick={() => navigate("/parent/fees")}>{t("guardian.backToFees")}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-16 px-4" data-testid="payment-result">
      <Card className="border-[var(--subtle)]">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 text-[var(--brand)]">
            <ShieldCheck className="h-5 w-5" />
            <span className="text-sm font-semibold">{t("guardian.securePaymentSim")}</span>
          </div>
          <div className="mt-4 rounded-lg border border-[var(--subtle)] p-4">
            <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
              <CreditCard className="h-4 w-4" />
              {t("guardian.hostedCheckout")}
            </div>
            <div className="mt-2 font-mono-num text-xs text-[var(--text-muted)]">{copy.payment} {paymentId.slice(0, 8)}</div>
            <div className="mt-4 grid grid-cols-1 gap-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--text-muted)]">{t("guardian.card")}</span>
                <span className="font-mono-num">•••• •••• •••• 4242</span>
              </div>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-3 gap-3">
            <Button variant="outline" size="sm" disabled={submit.isPending} onClick={() => submit.mutate("cancelled")}>
              {t("guardian.cancel")}
            </Button>
            <Button variant="outline" size="sm" disabled={submit.isPending} onClick={() => submit.mutate("failed")}>
              {t("guardian.fail")}
            </Button>
            <Button size="sm" className="w-full" disabled={submit.isPending} onClick={() => submit.mutate("paid")}>
              {submit.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} {t("guardian.payNow")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}